import { Component, EventEmitter, Inject, OnInit, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ErrorService, LayoutService } from '@core/services';
import { EmployerProfileStoreService } from '@core/services/employer-profile-store.service';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { Subject, Observable } from 'rxjs';
import { EmployerNotification, EmployerNotificationRecipientType } from '@core/models/leave-admin/employers/employer-profile.model';
import { 
  EditEmployerNotificationDialog, 
  EditEmployerNotificationForm, 
  EditEmployerNotificationPost, 
  RecipientsFormGroup } from '@core/models/leave-admin/employers/employer-notification.model';
import { takeUntil } from 'rxjs/operators';
import { StiiraError } from '@core/models';
import { AddNotifRecipientsComponent } from './components/add-notif-recipients/add-notif-recipients.component';
import { onDragEnded, position } from '@shared/helpers/dialog-drag-constraints';

@Component({
    selector: 'app-edit-employer-notification',
    templateUrl: './edit-employer-notification.component.html',
    styleUrl: './edit-employer-notification.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditEmployerNotificationComponent implements OnInit {
  static readonly componentName = 'EditEmployerNotificationComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  @ViewChild('child') toRecipients!: AddNotifRecipientsComponent;

  public usingDefaults: boolean = this.data.notification.usingDefaults;
  public form: FormGroup<EditEmployerNotificationForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();

  public position = position;
  public onDragEnded = onDragEnded;

  private formChangeEmitted: boolean = false;
  private formInitValues: any;

  private destroy$: Subject<void> = new Subject<void>();

  public isMobile$: Observable<boolean>;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  get enabled(): FormControl<boolean> {
    return this.form.controls.enabled;
  }

  get emailTemplate(): FormControl<number> {
    return this.form.controls.emailTemplateId;
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: {
      sysText: any;
      dialogData: EditEmployerNotificationDialog;
      notification: EmployerNotification;
      employerId: number;
    },
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<EditEmployerNotificationComponent>,
    private store: EmployerProfileStoreService,
    private dialog: MatDialog,
    private service: ManageEmployersService,
    private errorService: ErrorService,
  ) {
    this.form = this.fb.group<EditEmployerNotificationForm>({
      enabled: this.fb.control(null),
      emailTemplateId: this.fb.control(this.data.notification.emailTemplate?.id as number),

      to: this.fb.group<RecipientsFormGroup>({
        recipients: this.fb.control(this.data.notification.toRecipients ? 
          [...this.data.notification.toRecipients] : []),
        recipientTypeAdd: this.fb.control(
          this.data.notification.toRecipients?.filter(r => r.type === EmployerNotificationRecipientType.recipientType)
          .map(r => r.id + "." + r.emailAddressTypeId)),
        employeeAdd: this.fb.control(null),
        emailAddressAdd: this.fb.control(null)
      }),

      cc: this.fb.group<RecipientsFormGroup>({
        recipients: this.fb.control(this.data.notification.ccRecipients ? 
          [...this.data.notification.ccRecipients] : []),
        recipientTypeAdd: this.fb.control(
          this.data.notification.ccRecipients?.filter(r => r.type === EmployerNotificationRecipientType.recipientType)
          .map(r => r.id + "." + r.emailAddressTypeId)),
        employeeAdd: this.fb.control(null),
        emailAddressAdd: this.fb.control(null)
      }),

      bcc: this.fb.group<RecipientsFormGroup>({
        recipients: this.fb.control(this.data.notification.bccRecipients ? 
          [...this.data.notification.bccRecipients] : []),
        recipientTypeAdd: this.fb.control(
          this.data.notification.bccRecipients?.filter(r => r.type === EmployerNotificationRecipientType.recipientType)
          .map(r => r.id + "." + r.emailAddressTypeId)),
        employeeAdd: this.fb.control(null),
        emailAddressAdd: this.fb.control(null)
      })
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): void {
    const dto: EditEmployerNotificationPost = {
      employerId: this.data.employerId,
      notificationTypeId: this.data.notification.notificationTypeId,
      enabled: this.enabled.value,
      usingDefaults: this.usingDefaults,

      emailTemplateId: this.emailTemplate.value,
      to: this.form.controls.to.controls.recipients.value,
      cc: this.form.controls.cc.controls.recipients.value,
      bcc: this.form.controls.bcc.controls.recipients.value
    }
    this.service.postEditEmployerNotification(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        this.store.employerProfile = res;
        this.dialogRef.close();
    },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors))
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  public onRestoreDefaults(): void {
    // defaults includes a default email template,
    // a single default recipient type for Tos (empty employees and email addresses),
    // and empty CCs and BCCs

    this.form.controls.emailTemplateId.setValue(this.data.notification.defaultEmailTemplateId);

    var recipients = [ {
        type: EmployerNotificationRecipientType.recipientType,
        id: this.data.notification.defaultRecipientTypeId,
        title: this.data.dialogData.recipientTypeOptions.find(opt => opt.id == this.data.notification.defaultRecipientTypeId).name,
        companyName: null,
        description: this.data.dialogData.recipientTypeOptions.find(opt => opt.id == this.data.notification.defaultRecipientTypeId)?.description,
        emailAddressTypeId: this.data.dialogData.recipientTypeOptions.find(opt => opt.id == this.data.notification.defaultRecipientTypeId)?.emailAddressTypeId,
      } ];

    if (this.data.notification.defaultAdditionalRecipientTypeId != null) {
      recipients.push({
        type: EmployerNotificationRecipientType.recipientType,
        id: this.data.notification.defaultAdditionalRecipientTypeId,
        title: this.data.dialogData.recipientTypeOptions.find(opt => opt.id == this.data.notification.defaultAdditionalRecipientTypeId).name,
        companyName: null,
        description: this.data.dialogData.recipientTypeOptions.find(opt => opt.id == this.data.notification.defaultAdditionalRecipientTypeId)?.description,
        emailAddressTypeId: this.data.dialogData.recipientTypeOptions.find(opt => opt.id == this.data.notification.defaultAdditionalRecipientTypeId)?.emailAddressTypeId,
      });
    }

    var recipientTypeAdd : string[] = [ this.data.notification.defaultRecipientTypeId + "." + this.data.notification.defaultEmailAddressTypeId ];

    if (this.data.notification.defaultAdditionalRecipientTypeId != null) {
      recipientTypeAdd.push(this.data.notification.defaultAdditionalRecipientTypeId + "." + this.data.notification.defaultEmailAddressTypeId);
    }

    this.form.controls.to.setValue({
      recipients: recipients,
      recipientTypeAdd: recipientTypeAdd,
      employeeAdd: null,
      emailAddressAdd: null
    });

    this.form.controls.cc.setValue({
      recipients: [ ],
      recipientTypeAdd: null,
      employeeAdd: null,
      emailAddressAdd: null
    });

    this.form.controls.bcc.setValue({
      recipients: [ ],
      recipientTypeAdd: null,
      employeeAdd: null,
      emailAddressAdd: null
    });

    this.usingDefaults = true;
  }

  public onClear(): void {
    this.form.controls.enabled.setValue(false);
    this.form.controls.emailTemplateId.setValue(null);

    this.form.controls.to.setValue({
      recipients: [ ],
      recipientTypeAdd: null,
      employeeAdd: null,
      emailAddressAdd: null
    });

    this.form.controls.cc.setValue({
      recipients: [ ],
      recipientTypeAdd: null,
      employeeAdd: null,
      emailAddressAdd: null
    });

    this.form.controls.bcc.setValue({
      recipients: [ ],
      recipientTypeAdd: null,
      employeeAdd: null,
      emailAddressAdd: null
    });
  }

  private setupForm(): void {
    this.enabled.setValue(this.data.notification.enabled);

    setTimeout(() => {
      this.formInitValues = { ...this.form.value }
      this.form.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.formChangeEmitted && !this.noChanges) {
            this.isEditing.emit(true);
            this.formChangeEmitted = true;
          }
          else if (this.noChanges) {
            this.isEditing.emit(false);
            this.formChangeEmitted = false;
          }

          this.usingDefaults = false;
        });
    }, 0);
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.store.sysText.unsavedChanges,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }
}